export type CameraSettings = {
  sensitivity: number;
  fov: number;
  invertY: boolean;
  rollSuppression: number;
  hudScale: number;
  reducedMotion: boolean;
  masterVolume: number;
  combatVolume: number;
  movementVolume: number;
  announcerVolume: number;
  audioMuted: boolean;
  keyBindings: KeyBindings;
  colorMode: ColorMode;
  captionsEnabled: boolean;
  touchControlsEnabled: boolean;
  // Phase 170 — graphics & gameplay tuning toggles.
  shadowsEnabled: boolean;
  bloomIntensity: number; // 0..1
  particleDensity: number; // 0..1, scales particle counts/effects
};

export type InputAction =
  | 'forward'
  | 'back'
  | 'left'
  | 'right'
  | 'jump'
  | 'fire'
  | 'grenade'
  | 'pack'
  | 'mine'
  | 'scoreboard'
  | 'teamSwitch';

export type KeyBindings = Record<InputAction, string>;

export type ColorMode = 'default' | 'deuteranopia' | 'protanopia' | 'high-contrast';

export type CameraSettingsUpdate =
  | { kind: 'reset' }
  | { kind: 'toggleInvertY' }
  | { kind: 'toggleReducedMotion' }
  | { kind: 'toggleAudioMuted' }
  | { kind: 'step'; field: 'sensitivity' | 'fov' | 'rollSuppression' | 'hudScale' | 'masterVolume' | 'combatVolume' | 'movementVolume' | 'announcerVolume'; delta: number };

export const DEFAULT_CAMERA_SETTINGS: CameraSettings = {
  sensitivity: 1,
  fov: 84,
  invertY: false,
  rollSuppression: 1,
  hudScale: 1,
  reducedMotion: false,
  masterVolume: 0.8,
  combatVolume: 0.9,
  movementVolume: 0.65,
  announcerVolume: 0.8,
  audioMuted: false,
  keyBindings: {
    forward: 'KeyW',
    back: 'KeyS',
    left: 'KeyA',
    right: 'KeyD',
    jump: 'Space',
    fire: 'Mouse0',
    grenade: 'KeyF',
    pack: 'KeyR',
    mine: 'KeyG',
    scoreboard: 'Tab',
    teamSwitch: 'KeyY',
  },
  colorMode: 'default',
  captionsEnabled: false,
  touchControlsEnabled: false,
  shadowsEnabled: true,
  bloomIntensity: 0.6,
  particleDensity: 1,
};

export const DEFAULT_KEY_BINDINGS = DEFAULT_CAMERA_SETTINGS.keyBindings;

const COLOR_MODES: ColorMode[] = ['default', 'deuteranopia', 'protanopia', 'high-contrast'];
const KEY_BINDING_ACTIONS = Object.keys(DEFAULT_KEY_BINDINGS) as InputAction[];

const CAMERA_SETTINGS_STORAGE_KEY = 'tribes2.cameraSettings.v1';
const CAMERA_SETTINGS_EVENT = 'tribes2:camera-settings';

const CAMERA_SETTINGS_BOUNDS = {
  sensitivity: { min: 0.25, max: 2.5, step: 0.1 },
  fov: { min: 68, max: 108, step: 2 },
  rollSuppression: { min: 0, max: 1, step: 0.1 },
  hudScale: { min: 0.7, max: 1.4, step: 0.1 },
  masterVolume: { min: 0, max: 1, step: 0.1 },
  combatVolume: { min: 0, max: 1, step: 0.1 },
  movementVolume: { min: 0, max: 1, step: 0.1 },
  announcerVolume: { min: 0, max: 1, step: 0.1 },
  bloomIntensity: { min: 0, max: 1, step: 0.1 },
  particleDensity: { min: 0, max: 1, step: 0.1 },
} as const;

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function roundToStep(value: number, step: number) {
  return Number((Math.round(value / step) * step).toFixed(4));
}

function normalizeBoolean(value: unknown) {
  return typeof value === 'boolean' ? value : undefined;
}

function normalizeColorMode(value: unknown): ColorMode {
  return typeof value === 'string' && COLOR_MODES.includes(value as ColorMode)
    ? (value as ColorMode)
    : DEFAULT_CAMERA_SETTINGS.colorMode;
}

function normalizeKeyBindings(value: unknown): KeyBindings {
  const source = value && typeof value === 'object' ? value as Partial<Record<InputAction, unknown>> : {};
  const normalized: KeyBindings = { ...DEFAULT_KEY_BINDINGS };

  for (const action of KEY_BINDING_ACTIONS) {
    const binding = source[action];
    if (typeof binding === 'string' && binding.trim().length > 0) {
      normalized[action] = binding.trim();
    }
  }

  return normalized;
}

function normalizeNumber(value: unknown, fallback: number, min: number, max: number, step: number) {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return fallback;
  }

  return clamp(roundToStep(value, step), min, max);
}

export function normalizeCameraSettings(value: Partial<CameraSettings> | null | undefined): CameraSettings {
  const source = value ?? {};

  return {
    sensitivity: normalizeNumber(
      source.sensitivity,
      DEFAULT_CAMERA_SETTINGS.sensitivity,
      CAMERA_SETTINGS_BOUNDS.sensitivity.min,
      CAMERA_SETTINGS_BOUNDS.sensitivity.max,
      CAMERA_SETTINGS_BOUNDS.sensitivity.step,
    ),
    fov: normalizeNumber(
      source.fov,
      DEFAULT_CAMERA_SETTINGS.fov,
      CAMERA_SETTINGS_BOUNDS.fov.min,
      CAMERA_SETTINGS_BOUNDS.fov.max,
      CAMERA_SETTINGS_BOUNDS.fov.step,
    ),
    invertY: normalizeBoolean(source.invertY) ?? DEFAULT_CAMERA_SETTINGS.invertY,
    rollSuppression: normalizeNumber(
      source.rollSuppression,
      DEFAULT_CAMERA_SETTINGS.rollSuppression,
      CAMERA_SETTINGS_BOUNDS.rollSuppression.min,
      CAMERA_SETTINGS_BOUNDS.rollSuppression.max,
      CAMERA_SETTINGS_BOUNDS.rollSuppression.step,
    ),
    hudScale: normalizeNumber(
      source.hudScale,
      DEFAULT_CAMERA_SETTINGS.hudScale,
      CAMERA_SETTINGS_BOUNDS.hudScale.min,
      CAMERA_SETTINGS_BOUNDS.hudScale.max,
      CAMERA_SETTINGS_BOUNDS.hudScale.step,
    ),
    reducedMotion: normalizeBoolean(source.reducedMotion) ?? DEFAULT_CAMERA_SETTINGS.reducedMotion,
    masterVolume: normalizeNumber(
      source.masterVolume,
      DEFAULT_CAMERA_SETTINGS.masterVolume,
      CAMERA_SETTINGS_BOUNDS.masterVolume.min,
      CAMERA_SETTINGS_BOUNDS.masterVolume.max,
      CAMERA_SETTINGS_BOUNDS.masterVolume.step,
    ),
    combatVolume: normalizeNumber(
      source.combatVolume,
      DEFAULT_CAMERA_SETTINGS.combatVolume,
      CAMERA_SETTINGS_BOUNDS.combatVolume.min,
      CAMERA_SETTINGS_BOUNDS.combatVolume.max,
      CAMERA_SETTINGS_BOUNDS.combatVolume.step,
    ),
    movementVolume: normalizeNumber(
      source.movementVolume,
      DEFAULT_CAMERA_SETTINGS.movementVolume,
      CAMERA_SETTINGS_BOUNDS.movementVolume.min,
      CAMERA_SETTINGS_BOUNDS.movementVolume.max,
      CAMERA_SETTINGS_BOUNDS.movementVolume.step,
    ),
    announcerVolume: normalizeNumber(
      source.announcerVolume,
      DEFAULT_CAMERA_SETTINGS.announcerVolume,
      CAMERA_SETTINGS_BOUNDS.announcerVolume.min,
      CAMERA_SETTINGS_BOUNDS.announcerVolume.max,
      CAMERA_SETTINGS_BOUNDS.announcerVolume.step,
    ),
    audioMuted: normalizeBoolean(source.audioMuted) ?? DEFAULT_CAMERA_SETTINGS.audioMuted,
    keyBindings: normalizeKeyBindings(source.keyBindings),
    colorMode: normalizeColorMode(source.colorMode),
    captionsEnabled: normalizeBoolean(source.captionsEnabled) ?? DEFAULT_CAMERA_SETTINGS.captionsEnabled,
    touchControlsEnabled: normalizeBoolean(source.touchControlsEnabled) ?? DEFAULT_CAMERA_SETTINGS.touchControlsEnabled,
    // Phase 170 — graphics & gameplay tuning normalization.
    shadowsEnabled: normalizeBoolean(source.shadowsEnabled) ?? DEFAULT_CAMERA_SETTINGS.shadowsEnabled,
    bloomIntensity: normalizeNumber(
      source.bloomIntensity,
      DEFAULT_CAMERA_SETTINGS.bloomIntensity,
      CAMERA_SETTINGS_BOUNDS.bloomIntensity.min,
      CAMERA_SETTINGS_BOUNDS.bloomIntensity.max,
      CAMERA_SETTINGS_BOUNDS.bloomIntensity.step,
    ),
    particleDensity: normalizeNumber(
      source.particleDensity,
      DEFAULT_CAMERA_SETTINGS.particleDensity,
      CAMERA_SETTINGS_BOUNDS.particleDensity.min,
      CAMERA_SETTINGS_BOUNDS.particleDensity.max,
      CAMERA_SETTINGS_BOUNDS.particleDensity.step,
    ),
  };
}

export function readCameraSettings(): CameraSettings {
  if (typeof window === 'undefined') {
    return DEFAULT_CAMERA_SETTINGS;
  }

  try {
    const raw = window.localStorage.getItem(CAMERA_SETTINGS_STORAGE_KEY);
    if (!raw) {
      return DEFAULT_CAMERA_SETTINGS;
    }

    return normalizeCameraSettings(JSON.parse(raw) as Partial<CameraSettings>);
  } catch {
    return DEFAULT_CAMERA_SETTINGS;
  }
}

export function writeCameraSettings(settings: CameraSettings): CameraSettings {
  const normalized = normalizeCameraSettings(settings);

  if (typeof window === 'undefined') {
    return normalized;
  }

  try {
    window.localStorage.setItem(CAMERA_SETTINGS_STORAGE_KEY, JSON.stringify(normalized));
  } catch {
    // Ignore storage failures and keep the live settings in memory.
  }

  window.dispatchEvent(new CustomEvent<CameraSettings>(CAMERA_SETTINGS_EVENT, { detail: normalized }));
  return normalized;
}

export function getCameraSettings() {
  return readCameraSettings();
}

export function setCameraSettings(
  next:
    | Partial<CameraSettings>
    | CameraSettings
    | ((current: CameraSettings) => Partial<CameraSettings> | CameraSettings),
) {
  const current = readCameraSettings();
  const resolved = typeof next === 'function' ? next(current) : next;
  return writeCameraSettings(normalizeCameraSettings({ ...current, ...resolved }));
}

export function subscribeCameraSettings(listener: (settings: CameraSettings) => void) {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const handler = (event: Event) => {
    const detail = (event as CustomEvent<CameraSettings>).detail;
    if (detail) {
      listener(detail);
    }
  };

  window.addEventListener(CAMERA_SETTINGS_EVENT, handler as EventListener);
  return () => window.removeEventListener(CAMERA_SETTINGS_EVENT, handler as EventListener);
}

export function updateCameraSettings(update: CameraSettingsUpdate): CameraSettings {
  const current = readCameraSettings();

  switch (update.kind) {
    case 'reset':
      return writeCameraSettings(DEFAULT_CAMERA_SETTINGS);
    case 'toggleInvertY':
      return writeCameraSettings({ ...current, invertY: !current.invertY });
    case 'toggleReducedMotion':
      return writeCameraSettings({ ...current, reducedMotion: !current.reducedMotion });
    case 'toggleAudioMuted':
      return writeCameraSettings({ ...current, audioMuted: !current.audioMuted });
    case 'step': {
      const bounds = CAMERA_SETTINGS_BOUNDS[update.field];
      return writeCameraSettings({
        ...current,
        [update.field]: clamp(current[update.field] + update.delta, bounds.min, bounds.max),
      } as CameraSettings);
    }
    default:
      return current;
  }
}

export function describeCameraSettings(settings: CameraSettings) {
  const normalized = normalizeCameraSettings(settings);
  const compact = [];

  if (normalized.sensitivity !== DEFAULT_CAMERA_SETTINGS.sensitivity) {
    compact.push(`S${normalized.sensitivity.toFixed(1)}`);
  }

  if (normalized.fov !== DEFAULT_CAMERA_SETTINGS.fov) {
    compact.push(`F${Math.round(normalized.fov)}`);
  }

  if (normalized.invertY !== DEFAULT_CAMERA_SETTINGS.invertY) {
    compact.push(`IY`);
  }

  if (normalized.rollSuppression !== DEFAULT_CAMERA_SETTINGS.rollSuppression) {
    compact.push(`R${normalized.rollSuppression.toFixed(1)}`);
  }

  if (normalized.hudScale !== DEFAULT_CAMERA_SETTINGS.hudScale) {
    compact.push(`H${normalized.hudScale.toFixed(1)}`);
  }

  if (normalized.audioMuted) {
    compact.push('MUTE');
  } else if (normalized.masterVolume !== DEFAULT_CAMERA_SETTINGS.masterVolume) {
    compact.push(`V${normalized.masterVolume.toFixed(1)}`);
  }

  return compact.length > 0 ? `CAM ${compact.join(' ')}` : '';
}

export function cameraSettingsEventName() {
  return CAMERA_SETTINGS_EVENT;
}
